import React, { useState, useRef, useEffect } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { Language } from "../i18n/translations";
import { ChevronDown } from "lucide-react";

// Flag components
const RomanianFlag = () => (
  <svg viewBox="0 0 24 16" className="w-5 h-4 rounded-sm shadow-sm">
    <rect x="0" y="0" width="8" height="16" fill="#002B7F" />
    <rect x="8" y="0" width="8" height="16" fill="#FCD116" />
    <rect x="16" y="0" width="8" height="16" fill="#CE1126" />
  </svg>
);

const ItalianFlag = () => (
  <svg viewBox="0 0 24 16" className="w-5 h-4 rounded-sm shadow-sm">
    <rect x="0" y="0" width="8" height="16" fill="#009246" />
    <rect x="8" y="0" width="8" height="16" fill="#FFFFFF" />
    <rect x="16" y="0" width="8" height="16" fill="#CE2B37" />
  </svg>
);

const languages = [
  {
    code: "ro" as Language,
    name: "Română",
    shortName: "RO",
    Flag: RomanianFlag,
  },
  {
    code: "it" as Language,
    name: "Italiano",
    shortName: "IT",
    Flag: ItalianFlag,
  },
];

interface LanguageSwitcherProps {
  variant?: "header" | "mobile";
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  variant = "header",
}) => {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLang =
    languages.find((l) => l.code === language) || languages[0];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (lang: Language) => {
    setLanguage(lang);
    setIsOpen(false);
  };

  if (variant === "mobile") {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 px-4 py-2.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors w-full justify-between"
        >
          <div className="flex items-center space-x-2">
            <currentLang.Flag />
            <span className="text-sm font-medium text-gray-700">
              {currentLang.name}
            </span>
          </div>
          <ChevronDown
            className={`w-4 h-4 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50">
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang.code)}
                className={`flex items-center space-x-2 w-full px-4 py-2.5 hover:bg-gray-50 transition-colors ${
                  language === lang.code
                    ? "bg-romanian-blue/10 text-romanian-blue"
                    : "text-gray-700"
                }`}
              >
                <lang.Flag />
                <span className="text-sm font-medium">{lang.name}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-1.5 px-2.5 py-1.5 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
      >
        <currentLang.Flag />
        <span className="text-xs font-semibold text-gray-700">
          {currentLang.shortName}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 text-gray-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50 min-w-[120px]">
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleSelect(lang.code)}
              className={`flex items-center space-x-2 w-full px-3 py-2 hover:bg-gray-50 transition-colors ${
                language === lang.code
                  ? "bg-romanian-blue/10 text-romanian-blue"
                  : "text-gray-700"
              }`}
            >
              <lang.Flag />
              <span className="text-sm font-medium">{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher;
